import React, { useState, useEffect, useRef } from 'react';
import {
  Camera,
  ClipboardList,
  IndianRupee,
  Loader2,
  Ruler,
  Upload,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  deleteProductImageFromImageKit,
  uploadProductImage,
} from '../../firebase/storageHelpers';
import '../../pages/ProductManagement.css';
import './ProductForm.css';

const iconProps = { size: 18, strokeWidth: 1.75 };

const ProductForm = ({ mode, product, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    material: '',
    bagSize: {
      cm: '',
      inch: ''
    },
    specifications: {
      gsm: '',
      bf: ''
    },
    price: {
      amount: '',
      currency: 'Rs',
      unit: 'piece'
    },
    stock: 0,
    moq: 100,
    taxPercentage: 18,
    images: []
  });

  const [imageInput, setImageInput] = useState('');
  const [imageUploadCount, setImageUploadCount] = useState(0);
  const [removingImageIndex, setRemovingImageIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  /** Avoid closing when text selection ends with mouseup on the backdrop (click fires on overlay). */
  const overlayCloseAllowedRef = useRef(false);

  const imageUploading = imageUploadCount > 0;
  const imageBusy = imageUploading || removingImageIndex !== null;

  // Populate form if editing
  useEffect(() => {
    if (mode === 'edit' && product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category: product.category || '',
        material: product.material || '',
        bagSize: {
          cm: product.bagSize?.cm || '',
          inch: product.bagSize?.inch || ''
        },
        specifications: {
          gsm: product.specifications?.gsm != null ? String(product.specifications.gsm) : '',
          bf: product.specifications?.bf != null ? String(product.specifications.bf) : ''
        },
        price: {
          amount: product.price?.amount || '',
          currency: product.price?.currency || 'Rs',
          unit: product.price?.unit || 'piece'
        },
        stock: product.stock || 0,
        moq: product.moq || 100,
        taxPercentage:
          product.taxPercentage != null && product.taxPercentage !== ''
            ? product.taxPercentage
            : 18,
        images: product.images || []
      });
    }
  }, [mode, product]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleAddImage = () => {
    if (imageInput.trim()) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, imageInput.trim()]
      }));
      setImageInput('');
    }
  };

  const handleRemoveImage = async (index) => {
    if (imageBusy) return;
    const url = formData.images[index];
    if (url == null) return;

    const persisted =
      mode === 'edit' &&
      Array.isArray(product?.images) &&
      product.images.includes(url);

    const ok = window.confirm(
      persisted
        ? 'Remove this image from the product? It will be removed from ImageKit when you save.'
        : 'Remove this image? It will be taken off the list and the file will be removed from ImageKit.'
    );
    if (!ok) return;

    if (!persisted) {
      setRemovingImageIndex(index);
      try {
        try {
          await deleteProductImageFromImageKit(url);
        } catch (ikErr) {
          const d = ikErr?.response?.data;
          toast.warn(
            d?.details ||
              d?.error ||
              ikErr?.message ||
              'Could not delete the file from ImageKit; removing it from the list anyway.'
          );
        }
      } finally {
        setRemovingImageIndex(null);
      }
    }

    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, idx) => idx !== index),
    }));
  };

  const handleFileInputChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    let uploaded = 0;
    for (const file of files) {
      setImageUploadCount((c) => c + 1);
      try {
        const url = await uploadProductImage(file);
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, url],
        }));
        uploaded += 1;
      } catch (err) {
        const message =
          err?.message || 'Image upload failed. Check Storage rules and try again.';
        toast.error(`${file.name}: ${message}`);
      } finally {
        setImageUploadCount((c) => Math.max(0, c - 1));
      }
    }
    if (uploaded > 0) {
      toast.success(
        uploaded === 1
          ? '1 image uploaded to ImageKit.'
          : `${uploaded} images uploaded to ImageKit.`
      );
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.category.trim()) newErrors.category = 'Category is required';
    if (!formData.material.trim()) newErrors.material = 'Material is required';
    if (!formData.bagSize.cm.trim()) newErrors.bagSizeCm = 'Size (cm) is required';
    if (!formData.bagSize.inch.trim()) newErrors.bagSizeInch = 'Size (inch) is required';
    if (!formData.specifications.gsm) newErrors.gsm = 'GSM is required';
    if (!formData.specifications.bf) newErrors.bf = 'BF is required';
    if (!formData.price.amount || formData.price.amount <= 0) newErrors.price = 'Valid price is required';
    if (formData.stock < 0) newErrors.stock = 'Stock cannot be negative';
    if (formData.moq <= 0) newErrors.moq = 'MOQ must be greater than 0';
    const tp = Number(formData.taxPercentage);
    if (!Number.isFinite(tp) || tp < 0 || tp > 100) {
      newErrors.taxPercentage = 'Tax must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (imageBusy) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (mode === 'edit' && product?.images?.length) {
      const removed = product.images.filter((u) => !formData.images.includes(u));
      for (const u of removed) {
        try {
          await deleteProductImageFromImageKit(u);
        } catch (ikErr) {
          const d = ikErr?.response?.data;
          toast.warn(
            d?.details ||
              d?.error ||
              ikErr?.message ||
              'Could not delete a file from ImageKit; saving the product anyway.'
          );
        }
      }
    }

    // API schema: specifications.gsm/bf are strings; stock may be a number (backend maps to stock.quantity)
    const submitData = {
      ...formData,
      specifications: {
        gsm: String(formData.specifications.gsm ?? '').trim(),
        bf: String(formData.specifications.bf ?? '').trim()
      },
      price: {
        ...formData.price,
        amount: Number(formData.price.amount),
        unit: String(formData.price.unit || '').toLowerCase(),
        currency: String(formData.price.currency || '').trim()
      },
      stock: Number(formData.stock),
      moq: Number(formData.moq),
      taxPercentage: (() => {
        const v = Number(formData.taxPercentage);
        return Number.isFinite(v)
          ? Math.max(0, Math.min(100, v))
          : 18;
      })()
    };

    await Promise.resolve(onSubmit(submitData));
  };

  const handleOverlayMouseDown = (e) => {
    overlayCloseAllowedRef.current = e.target === e.currentTarget;
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && overlayCloseAllowedRef.current) {
      onClose();
    }
    overlayCloseAllowedRef.current = false;
  };

  let submitButtonLabel = mode === 'add' ? 'Create Product' : 'Update Product';
  if (imageUploading) {
    submitButtonLabel = 'Wait for uploads…';
  } else if (removingImageIndex !== null) {
    submitButtonLabel = 'Removing image…';
  }

  return (
    <div
      className="pm-modal-overlay"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pm-modal-header">
          <h2 className="pm-modal-title">
            {mode === 'add' ? 'Add New Product' : 'Edit Product'}
          </h2>
          <button type="button" className="pm-modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <form className="pf-product-form" onSubmit={handleSubmit}>
          <div className="pm-modal-content">
            {/* Product Images — same order and title pattern as Product Details */}
            <div className="pm-product-detail-section">
              <h3 className="pm-section-title">
                <Camera {...iconProps} className="pm-section-title-icon" aria-hidden />
                Product Images
              </h3>
              <div className="pf-image-manager">
                <div className="pf-image-input-group">
                  <input
                    type="text"
                    className="pf-form-input"
                    value={imageInput}
                    onChange={(e) => setImageInput(e.target.value)}
                    placeholder="Paste image URL..."
                    disabled={imageBusy}
                  />
                  <button
                    type="button"
                    className="pf-btn-add-image"
                    onClick={handleAddImage}
                    disabled={imageBusy}
                  >
                    Add URL
                  </button>
                </div>

                <div className="pf-image-upload-row">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="pf-file-input-hidden"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    multiple
                    onChange={handleFileInputChange}
                    tabIndex={-1}
                    aria-label="Choose product images to upload"
                  />
                  <button
                    type="button"
                    className="pf-btn-upload-file"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={imageBusy}
                  >
                    <Upload {...iconProps} className="pf-btn-upload-icon" aria-hidden />
                    {imageUploading ? 'Uploading…' : 'Upload from device'}
                  </button>
                  <span className="pf-upload-hint">
                    JPEG, PNG, GIF, or WebP · up to 10MB each · delivered via ImageKit
                  </span>
                </div>

                {formData.images.length > 0 ? (
                  <div className="pf-image-list">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="pf-image-item">
                        <img src={img} alt={`Product ${idx + 1}`} className="pf-image-preview" />
                        <button
                          type="button"
                          className="pf-btn-remove-image"
                          onClick={() => handleRemoveImage(idx)}
                          disabled={imageBusy}
                          title="Remove image"
                          aria-label={`Remove image ${idx + 1}`}
                        >
                          {removingImageIndex === idx ? (
                            <Loader2
                              {...iconProps}
                              className="pm-gallery-delete-spinner"
                              aria-hidden
                            />
                          ) : (
                            '\u00d7'
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="pf-no-images-hint">
                    No images yet — paste a URL, or upload files from your device.
                  </div>
                )}
              </div>
            </div>

            {/* Product Information — mirrors Product Details grid */}
            <div className="pm-product-detail-section">
              <h3 className="pm-section-title">
                <ClipboardList {...iconProps} className="pm-section-title-icon" aria-hidden />
                Product Information
              </h3>
              <div className="pf-form-grid">
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    Product name <span className="pf-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`pf-form-input ${errors.name ? 'pf-error' : ''}`}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g., Premium Paper Bag"
                  />
                  {errors.name && <span className="pf-error-text">{errors.name}</span>}
                </div>
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    Category <span className="pf-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`pf-form-input ${errors.category ? 'pf-error' : ''}`}
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    placeholder="e.g., Shopping Bags"
                  />
                  {errors.category && <span className="pf-error-text">{errors.category}</span>}
                </div>
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    Material <span className="pf-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`pf-form-input ${errors.material ? 'pf-error' : ''}`}
                    value={formData.material}
                    onChange={(e) => handleChange('material', e.target.value)}
                    placeholder="e.g., Kraft Paper"
                  />
                  {errors.material && <span className="pf-error-text">{errors.material}</span>}
                </div>
                <div className="pf-form-group pf-full-width">
                  <label className="pf-form-label">
                    Description <span className="pf-required">*</span>
                  </label>
                  <textarea
                    className={`pf-form-textarea ${errors.description ? 'pf-error' : ''}`}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Detailed product description..."
                    rows="3"
                  />
                  {errors.description && <span className="pf-error-text">{errors.description}</span>}
                </div>
              </div>
            </div>

            {/* Size & Specifications — single section like Product Details */}
            <div className="pm-product-detail-section">
              <h3 className="pm-section-title">
                <Ruler {...iconProps} className="pm-section-title-icon" aria-hidden />
                Size &amp; Specifications
              </h3>
              <div className="pf-form-grid">
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    Size (cm) <span className="pf-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`pf-form-input ${errors.bagSizeCm ? 'pf-error' : ''}`}
                    value={formData.bagSize.cm}
                    onChange={(e) => handleNestedChange('bagSize', 'cm', e.target.value)}
                    placeholder="e.g., 25x30x10"
                  />
                  {errors.bagSizeCm && <span className="pf-error-text">{errors.bagSizeCm}</span>}
                </div>
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    Size (inch) <span className="pf-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`pf-form-input ${errors.bagSizeInch ? 'pf-error' : ''}`}
                    value={formData.bagSize.inch}
                    onChange={(e) => handleNestedChange('bagSize', 'inch', e.target.value)}
                    placeholder="e.g., 10x12x4"
                  />
                  {errors.bagSizeInch && <span className="pf-error-text">{errors.bagSizeInch}</span>}
                </div>
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    GSM <span className="pf-required">*</span>
                  </label>
                  <input
                    type="number"
                    className={`pf-form-input ${errors.gsm ? 'pf-error' : ''}`}
                    value={formData.specifications.gsm}
                    onChange={(e) => handleNestedChange('specifications', 'gsm', e.target.value)}
                    placeholder="e.g., 120"
                  />
                  {errors.gsm && <span className="pf-error-text">{errors.gsm}</span>}
                </div>
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    BF (bursting factor) <span className="pf-required">*</span>
                  </label>
                  <input
                    type="number"
                    className={`pf-form-input ${errors.bf ? 'pf-error' : ''}`}
                    value={formData.specifications.bf}
                    onChange={(e) => handleNestedChange('specifications', 'bf', e.target.value)}
                    placeholder="e.g., 25"
                  />
                  {errors.bf && <span className="pf-error-text">{errors.bf}</span>}
                </div>
              </div>
            </div>

            {/* Stock & Pricing — price row: three columns; stock row: two columns */}
            <div className="pm-product-detail-section">
              <h3 className="pm-section-title">
                <IndianRupee {...iconProps} className="pm-section-title-icon" aria-hidden />
                Stock &amp; Pricing
              </h3>
              <div className="pf-form-grid pf-form-grid-pricing">
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    Unit price <span className="pf-required">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className={`pf-form-input ${errors.price ? 'pf-error' : ''}`}
                    value={formData.price.amount}
                    onChange={(e) => handleNestedChange('price', 'amount', e.target.value)}
                    placeholder="e.g., 5.50"
                  />
                  {errors.price && <span className="pf-error-text">{errors.price}</span>}
                </div>
                <div className="pf-form-group">
                  <label className="pf-form-label">Currency</label>
                  <select
                    className="pf-form-select"
                    value={formData.price.currency}
                    onChange={(e) => handleNestedChange('price', 'currency', e.target.value)}
                  >
                    <option value="Rs">Rs</option>
                    <option value="$">$</option>
                    <option value="€">€</option>
                  </select>
                </div>
                <div className="pf-form-group">
                  <label className="pf-form-label">Unit</label>
                  <select
                    className="pf-form-select"
                    value={formData.price.unit}
                    onChange={(e) => handleNestedChange('price', 'unit', e.target.value)}
                  >
                    <option value="piece">Piece</option>
                    <option value="dozen">Dozen</option>
                    <option value="box">Box</option>
                  </select>
                </div>
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    Tax (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    className={`pf-form-input ${errors.taxPercentage ? 'pf-error' : ''}`}
                    value={formData.taxPercentage}
                    onChange={(e) => handleChange('taxPercentage', e.target.value)}
                    placeholder="e.g., 18"
                  />
                  {errors.taxPercentage && (
                    <span className="pf-error-text">{errors.taxPercentage}</span>
                  )}
                  <span className="pf-field-hint">
                    Applied to line subtotal at checkout (GST/VAT).
                  </span>
                </div>
              </div>
              <div className="pf-form-grid pf-form-grid-stock">
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    Stock quantity <span className="pf-required">*</span>
                  </label>
                  <input
                    type="number"
                    className={`pf-form-input ${errors.stock ? 'pf-error' : ''}`}
                    value={formData.stock}
                    onChange={(e) => handleChange('stock', e.target.value)}
                    placeholder="e.g., 1000"
                  />
                  {errors.stock && <span className="pf-error-text">{errors.stock}</span>}
                </div>
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    Minimum order quantity (MOQ) <span className="pf-required">*</span>
                  </label>
                  <input
                    type="number"
                    className={`pf-form-input ${errors.moq ? 'pf-error' : ''}`}
                    value={formData.moq}
                    onChange={(e) => handleChange('moq', e.target.value)}
                    placeholder="e.g., 100"
                  />
                  {errors.moq && <span className="pf-error-text">{errors.moq}</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="pm-modal-actions">
            <button type="button" className="pm-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="pm-btn-primary"
              disabled={imageBusy}
            >
              {submitButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
